import { getCarouselItems } from "./carousel-item";
import { CarouselItem } from "./carousel-types";
import { getNextMeeting, getNextTurnersTalk } from "../events/events";
import { getLatestVideo } from "../video";
import { getLatestNewsletter } from "../newsletters/newsletter";

async function buildSlide(item: CarouselItem): Promise<string> {
  const html = await buildSlideContent(item);
  return applyBackground(html, item.background);
}

async function buildSlideContent(item: CarouselItem): Promise<string> {
  switch (item.type) {
    case "video":
      return buildVideoSlide(item);
    case "event":
      return buildEventSlide(item);
    case "featured_turning":
      return buildFeaturedTurningSlide(item);
    case "latest_newsletter":
      return buildLatestNewsletterSlide();
    case "latest_video":
      return buildLatestVideoSlide();
    case "membership":
      return buildMembershipSlide();
    case "next_meeting":
      return buildNextMeetingSlide();
    case "post":
      return buildPostSlide(item);
    default:
      return "";
  }
}

// A carousel item's `background` (a full image URL) is applied as an inline
// style on the slide's own outer element, rather than in the class-based
// scss, so it can override any type's default look (e.g. the hardcoded
// meeting/membership images) regardless of slide type.
function applyBackground(html: string, background: string | null): string {
  if (!background || !html) return html;

  return html.replace(
    /^(\s*<[a-zA-Z][^\s>]*)/,
    `$1 style="background-image: url('${background}')"`,
  );
}

function buildVideoSlide(item: CarouselItem): string {
  return `<div class="carousel-slide video">${JSON.stringify(item.video)}</div>`;
}

function buildEventSlide(item: CarouselItem): string {
  return `<div class="carousel-slide event">${JSON.stringify(item.event)}</div>`;
}

function buildFeaturedTurningSlide(item: CarouselItem): string {
  const featuredTurning = item.featured_turning;
  if (!featuredTurning) return "";

  return `
    <a href="${featuredTurning.url}" class="carousel-slide featured-turning">
      <div class="image-frame">
        <img src="${featuredTurning.image}" alt="${featuredTurning.description}" loading="lazy">
        <div class="caption-card">
          <p class="description">${featuredTurning.description}</p>
          <p class="contributor">${featuredTurning.contributor}</p>
        </div>
      </div>
    </a>
  `;
}

// The image-frame's size is driven by this custom property (see
// _carousel.scss) so it can shrink to exactly the visible, letterboxed area
// of the photo - only then can the caption be pinned to the image itself
// rather than to empty letterbox space.
function applyFeaturedTurningAspectRatios(track: HTMLElement): void {
  track
    .querySelectorAll<HTMLImageElement>(".featured-turning .image-frame img")
    .forEach((img) => {
      const setAspectRatio = () => {
        img.parentElement?.style.setProperty(
          "--image-aspect-ratio",
          `${img.naturalWidth} / ${img.naturalHeight}`,
        );
      };

      if (img.complete) {
        setAspectRatio();
      } else {
        img.addEventListener("load", setAspectRatio, { once: true });
      }
    });
}

function buildMembershipSlide(): string {
  return `
    <a href="/membership" class="carousel-slide membership">
      <div class="membership-card">
        <h3>Become a Member</h3>
        <ul>
          <li>Monthly demos from skilled turners</li>
          <li>Hands-on clinics and mentorship</li>
          <li>Access to the tool lending library</li>
          <li>A friendly community of fellow woodturners</li>
          <li>Discounts on woodturning supplies</li>
        </ul>
        <p class="learn-more"><span>Learn More</span></p>
      </div>
    </a>
  `;
}

function buildPostSlide(item: CarouselItem): string {
  return `<div class="carousel-slide post">${JSON.stringify(item.post)}</div>`;
}

async function buildNextMeetingSlide(): Promise<string> {
  const [meeting, turnersTalk] = await Promise.all([
    getNextMeeting(),
    getNextTurnersTalk(),
  ]);
  if (!meeting) return "";

  const date = meeting.startTime.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const meetingTime = meeting.startTime.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
  const turnersTalkTime = turnersTalk?.startTime.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });

  // The Turners Talk is only relevant here when it's part of this same
  // meeting; if the next scheduled talk falls on a different day, it
  // belongs to a different meeting and shouldn't be shown alongside this one.
  const showTurnersTalk =
    turnersTalk !== null &&
    turnersTalk.startTime.toDateString() === meeting.startTime.toDateString();

  return `
    <a href="/meetings" class="carousel-slide meeting">
      <div class="meeting-card">
        <p>Join us on <span class="date">${date}</span>!</p>
        ${
          showTurnersTalk
            ? `
          <h3>${turnersTalkTime} Turners Talk</h3>
          ${turnersTalk.topic ? `<p>${turnersTalk.topic}</p>` : ""}
        `
            : ""
        }
        <h3>${meetingTime} Monthly Meeting</h3>
        ${meeting.topic ? `<p>${meeting.topic}</p>` : ""}
      </div>
    </a>
  `;
}

async function buildLatestVideoSlide(): Promise<string> {
  const video = await getLatestVideo();
  if (!video) return "";

  return `
    <div class="carousel-slide video">
      <a href="https://www.youtube.com/watch?v=${video.videoId}" target="_blank" rel="noreferrer noopener">
        <img src="${video.thumbnailUrl}" alt="${video.title}" loading="lazy">
      </a>
    </div>
  `;
}

async function buildLatestNewsletterSlide(): Promise<string> {
  const newsletterUrl = await getLatestNewsletter();
  if (!newsletterUrl) return "";

  return `
    <div class="carousel-slide newsletter">
      <a href="${newsletterUrl}" target="_blank" rel="noreferrer noopener">Latest Newsletter</a>
    </div>
  `;
}

const DEFAULT_SLIDE_DURATION_MS = 6000;
const MAX_SLIDE_DURATION_OVERRIDE_SECONDS = 30;
const RESUME_DELAY_MS = 15000;

interface CarouselState {
  track: HTMLElement;
  slideCount: number;
  durationsMs: number[];
  activeIndex: number;
  rotationTimeoutId: number | null;
  resumeTimeoutId: number | null;
}

// An item's `duration` (seconds) overrides the default, but never shortens
// it below the default, and is capped so a single misconfigured item can't
// stall the carousel indefinitely.
function resolveDurationMs(item: CarouselItem): number {
  if (item.duration == null) return DEFAULT_SLIDE_DURATION_MS;

  const cappedSeconds = Math.min(
    item.duration,
    MAX_SLIDE_DURATION_OVERRIDE_SECONDS,
  );
  return Math.max(DEFAULT_SLIDE_DURATION_MS, cappedSeconds * 1000);
}

// The track is bookended with a clone of the last slide and a clone of the
// first slide (see initializeCarousels), so it always holds two more
// elements than there are real slides.
function trackSlideCount(state: CarouselState): number {
  return state.slideCount + 2;
}

export function initializeCarousels(): void {
  document.querySelectorAll<HTMLElement>(".carousel").forEach((container) => {
    getCarouselItems().then(async (items) => {
      const built = await Promise.all(
        items.map(async (item) => ({
          html: await buildSlide(item),
          durationMs: resolveDurationMs(item),
        })),
      );
      const rendered = built.filter((slide) => slide.html !== "");
      if (rendered.length === 0) return;

      const slides = rendered.map((slide) => slide.html);
      const durationsMs = rendered.map((slide) => slide.durationMs);

      const track = document.createElement("div");
      track.className = "carousel-track";
      // Clones of the last and first slides bookend the track so it can be
      // dragged past either end, then snapped back to the matching real
      // slide once the clone is fully in view.
      track.innerHTML =
        slides.length > 1
          ? slides[slides.length - 1] + slides.join("") + slides[0]
          : slides[0];

      container.innerHTML = "";
      container.appendChild(track);
      applyFeaturedTurningAspectRatios(track);

      if (slides.length <= 1) return;

      const state: CarouselState = {
        track,
        slideCount: slides.length,
        durationsMs,
        activeIndex: 1,
        rotationTimeoutId: null,
        resumeTimeoutId: null,
      };

      // The track's width and each slide's width are expressed as explicit
      // fractions (via this custom property) of the container, rather than
      // relying on the browser to size an overflowing flex row implicitly -
      // translateX() percentages below are resolved against the track's own
      // width, so that width has to be exact for the offsets to line up.
      track.style.setProperty("--slide-count", String(trackSlideCount(state)));

      setSlide(state, 1, false);
      buildCarouselControls(container, state);
      startAutoRotation(state);
    });
  });
}

function setSlide(state: CarouselState, index: number, animate: boolean): void {
  const offsetPercent = (index / trackSlideCount(state)) * 100;

  state.track.style.transition = animate ? "" : "none";
  state.track.style.transform = `translateX(-${offsetPercent}%)`;

  if (!animate) {
    void state.track.offsetHeight;
    state.track.style.transition = "";
  }

  state.activeIndex = index;
}

function advance(state: CarouselState, direction: 1 | -1): void {
  const nextIndex = state.activeIndex + direction;
  setSlide(state, nextIndex, true);

  if (nextIndex === state.slideCount + 1) {
    state.track.addEventListener(
      "transitionend",
      () => setSlide(state, 1, false),
      {
        once: true,
      },
    );
  } else if (nextIndex === 0) {
    state.track.addEventListener(
      "transitionend",
      () => setSlide(state, state.slideCount, false),
      { once: true },
    );
  }
}

// activeIndex spans the bookend clones too (0 and slideCount + 1), which
// stand in for the last and first real slides respectively, so their
// duration comes from that same real slide.
function activeSlideDurationMs(state: CarouselState): number {
  const realIndex =
    (((state.activeIndex - 1) % state.slideCount) + state.slideCount) %
    state.slideCount;
  return state.durationsMs[realIndex] ?? DEFAULT_SLIDE_DURATION_MS;
}

function startAutoRotation(state: CarouselState): void {
  state.rotationTimeoutId = window.setTimeout(() => {
    advance(state, 1);
    startAutoRotation(state);
  }, activeSlideDurationMs(state));
}

function stopAutoRotation(state: CarouselState): void {
  if (state.rotationTimeoutId !== null) {
    window.clearTimeout(state.rotationTimeoutId);
    state.rotationTimeoutId = null;
  }
}

function handleManualNavigation(state: CarouselState, direction: 1 | -1): void {
  stopAutoRotation(state);
  if (state.resumeTimeoutId !== null) {
    window.clearTimeout(state.resumeTimeoutId);
  }

  advance(state, direction);

  state.resumeTimeoutId = window.setTimeout(() => {
    state.resumeTimeoutId = null;
    startAutoRotation(state);
  }, RESUME_DELAY_MS);
}

function buildCarouselControls(
  container: HTMLElement,
  state: CarouselState,
): void {
  const prevButton = document.createElement("button");
  prevButton.type = "button";
  prevButton.className = "carousel-caret carousel-caret-prev";
  prevButton.setAttribute("aria-label", "Previous slide");
  prevButton.textContent = "‹";
  prevButton.addEventListener("click", () => handleManualNavigation(state, -1));

  const nextButton = document.createElement("button");
  nextButton.type = "button";
  nextButton.className = "carousel-caret carousel-caret-next";
  nextButton.setAttribute("aria-label", "Next slide");
  nextButton.textContent = "›";
  nextButton.addEventListener("click", () => handleManualNavigation(state, 1));

  container.appendChild(prevButton);
  container.appendChild(nextButton);
}
